/* eslint-disable immutable/no-mutation */
import React, {PropsWithChildren} from 'react'
import PropTypes from 'prop-types'
import {InstUISettingsProvider} from '@instructure/emotion'
import {setActiveTheme} from '@instructure/quiz-core'
import type {Theme} from '@instructure/ui-themes'

const getBaseTheme = async (highContrast: boolean) => {
  const themes = await import('@instructure/ui-themes')
  if (highContrast) {
    return themes.canvasHighContrastThemeLocal
  } else {
    return themes.canvasThemeLocal
  }
}

export interface ThemeProviderProps {
  rldbLaunch?: string | null
  highContrast?: boolean
  themeOverrides?: Record<string, string> | null
}

export function ThemeProvider({
  rldbLaunch = null,
  highContrast = false,
  themeOverrides = null,
  children,
}: PropsWithChildren<ThemeProviderProps>) {
  const [theme, setTheme] = React.useState<Theme | null>(null)
  const getThemeFromParent = React.useCallback((event: MessageEvent) => {
    if (!event.data?.type?.startsWith('quiz.')) {
      return
    }

    const {theme, type, origin} = event.data || {}

    if (type === 'quiz.setTheme' && theme) {
      if (theme.typography.fontSources) {
        theme.typography.fontSources.forEach((fontSource: string) => {
          const fontUrl = `${origin}/${fontSource}`
          const link = document.createElement('link')

          link.rel = 'stylesheet'
          link.href = fontUrl
          link.crossOrigin = 'anonymous'

          document.head.appendChild(link)
        })
      }

      setActiveTheme(theme)
      setTheme(theme)
    }
  }, [])

  React.useEffect(() => {
    getBaseTheme(highContrast).then(baseTheme => {
      setActiveTheme(baseTheme)
      setTheme(baseTheme)

      window.addEventListener('message', getThemeFromParent)

      // Note: We only send this message if not in Respondus LockDown Browser
      if (rldbLaunch == null || rldbLaunch === 'false') {
        window.parent.postMessage({type: 'quiz.waitingForTheme'}, '*')
      }
    })

    return () => {
      window.removeEventListener('message', getThemeFromParent)
    }
  }, [rldbLaunch, highContrast, getThemeFromParent])

  if (!theme) {
    return null
  }

  return (
    <InstUISettingsProvider theme={{...theme, ...(themeOverrides || {})}}>
      {children}
    </InstUISettingsProvider>
  )
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  highContrast: PropTypes.bool,
  rldbLaunch: PropTypes.string,
}
